import React, { useState, useEffect } from 'react';
import { connect } from 'react-redux';
import { makeStyles } from '@mui/styles';
import { Box, Typography } from '@mui/material';
import { useTheme } from '@mui/styles';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import Grid from '@mui/material/Grid';
import { campareDates } from '../Utilities/Calender';
import ChargingStrategySummaryComponent from './ChargingStrategies/ChargingStrategySummaryComponent';
import { utilService } from '../services/util.service';
import translationService from '../services/translator.service';
import { AppAccordion, AppAccordionSummary, AppAccordionDetails } from './primitives/AppAccordion';
import AppMenuMyChargePointsIcon from './shared/common/icons/AppMenuMyChargePointsIcon';
import ChargingHistoryClockIcon from './shared/common/icons/ChargingHistoryClockIcon';
import ChargingHistoryFlashIcon from './shared/common/icons/ChargingHistoryFlashIcon';
import ChargingHistoryCardIcon from './shared/common/icons/ChargingHistoryCardIcon';
import BillingInvoicesTabIcon from './shared/common/icons/BillingInvoicesTabIcon';
import AppButton from './primitives/AppButton';
import { isNative } from '../platform/env';
import { openInAppBrowser } from '../platform/inAppBrowser';

const TRANSACTION_ICON_SLOT_WIDTH_PX = 18;
const TRANSACTION_ICON_SIZE_PX = 14;
const TRANSACTION_ICON_VALUE_GAP_PX = 6;
// Same size the invoices tab (BillsTopLinks) renders this icon at.
const INVOICE_ICON_SIZE_PX = 20;

// spec 000230 (task 29) - the invoice of this exact session (ChargingSummery.InvoiceUrl, taken from the
// newest succeeded PaymentTransactions row of the session; null when there is none). Native opens the
// in-app browser; the web opens a new tab, because openInAppBrowser's web fallback assigns
// window.location and would navigate away from the SPA.
const hasInvoiceUrl = (item) => typeof item?.invoiceUrl === 'string' && item.invoiceUrl.trim() !== '';

const openInvoice = (event, invoiceUrl) => {
    event.stopPropagation();
    if (isNative) {
        openInAppBrowser(invoiceUrl).catch(() => {});
        return;
    }
    window.open(invoiceUrl, '_blank', 'noopener,noreferrer');
};

const VisualStepperComponent = ({ options, newInTop, languageReducer, dataAlreadySorted, sortDirection, ...props }) => {
    const theme = useTheme();
    const classes = useStyles(props);
    const [translatedData, setTranslatedData] = useState();
    // spec 000196 (F-17) — the cost-breakdown labels live in the chargingAction dictionary, not this
    // screen's own. Loading it here is what makes those labels appear at all.
    const [chargingActionTranslations, setChargingActionTranslations] = useState();
    const iconOptions = {
        size: TRANSACTION_ICON_SIZE_PX,
        activeColor: theme.palette.text.primary,
        inactiveColor: theme.palette.text.primary,
    };

    useEffect(() => {
        setTranslatedData(translationService.getTranslatedData(languageReducer.language, 'visualStepperComponent'));
        setChargingActionTranslations(translationService.getTranslatedData(languageReducer.language, 'chargingAction'));
    }, [languageReducer.language]);

    const sortData = (options) => {
        return sortDirection === 'ASC' ? options.sort((a, b) => campareDates(a.startDate, b.startDate)) : options.sort((a, b) => campareDates(b.startDate, a.startDate));
    };

    const getDuration = (item) => {
        // Prefer machine-readable timestamps from server SignalR payload.
        // Handle possible casing differences from JSON serialization.
        const startTs = item?.dateTime_StartCharging ?? item?.DateTime_StartCharging ?? item?.dateTimeStartCharging ?? item?.DateTimeStartCharging;

        const endTs = item?.dateTime_EndCharging ?? item?.DateTime_EndCharging ?? item?.dateTimeEndCharging ?? item?.DateTimeEndCharging;

        const durationFromTimestamps = utilService.calculateDurationHHMMSS(startTs, endTs);
        if (durationFromTimestamps) return durationFromTimestamps;

        // Fallback: legacy string fields (may be locale-formatted)
        const legacyDuration = utilService.calculateDurationHHMMSS(item?.startDate, item?.endDate);
        if (legacyDuration) return legacyDuration;

        // Last resort: keep existing behavior (some screens may still rely on DD/MM swapping)
        return utilService.calculateDateDifference(item?.startDate, item?.endDate);
    };

    const getDateTimeParts = (dateTimeValue) => {
        const rawValue = `${dateTimeValue || ''}`.trim();
        if (!rawValue) {
            return { date: '-', time: '-' };
        }

        const dateMatch = rawValue.match(/\d{2}[/-]\d{2}[/-]\d{4}/);
        const timeMatch = rawValue.match(/\d{2}:\d{2}:\d{2}/);

        if (dateMatch || timeMatch) {
            return {
                date: dateMatch ? dateMatch[0] : '-',
                time: timeMatch ? timeMatch[0] : '-',
            };
        }

        const chunks = rawValue.split(' ').filter(Boolean);
        if (chunks.length >= 2) {
            return { date: chunks[0], time: chunks[1] };
        }

        return { date: rawValue, time: '-' };
    };

    const getTransactionIconProps = () => {
        const iconColor = iconOptions.activeColor;
        return {
            size: iconOptions.size,
            primaryColor: iconColor,
            secondaryColor: iconColor,
            textColor: iconColor,
            strokeColor: iconColor,
        };
    };

    return (
        <Box className={classes.listWrapper}>
            {options &&
                sortData(options).map((item, index) => {
                    const startDateTime = getDateTimeParts(item.startDate);
                    const endDateTime = getDateTimeParts(item.endDate);

                    return (
                        <div key={index} className={classes.root}>
                            <AppAccordion>
                                <AppAccordionSummary expandIcon={<ExpandMoreIcon />} aria-controls='panel1a-content' id='panel1a-header'>
                                    <Box className={classes.summaryRow}>
                                        <Box className={classes.timesContainer}>
                                            <Box className={classes.timelineRail}>
                                                <Box className={classes.timelineDot} />
                                                <Box className={classes.timelineConnector} />
                                                <Box className={classes.timelineIconWrap}>
                                                    <AppMenuMyChargePointsIcon size={20} />
                                                </Box>
                                                <Box className={classes.timelineConnector} />
                                                <Box className={classes.timelineDot} />
                                            </Box>

                                            <Box className={classes.timelineDates}>
                                                <Typography variant='subtitle2'>{startDateTime.date}</Typography>
                                                <Typography variant='subtitle2'>{startDateTime.time}</Typography>
                                                <Typography variant='subtitle2'>{endDateTime.date}</Typography>
                                                <Typography variant='subtitle2'>{endDateTime.time}</Typography>
                                            </Box>
                                        </Box>

                                        <Box className={classes.transactionContent}>
                                            <Box className={classes.transactionMetrics}>
                                                <Box className={classes.transactionMetricRow}>
                                                    <Box className={classes.transactionMetricIcon}>
                                                        <ChargingHistoryClockIcon {...getTransactionIconProps()} />
                                                    </Box>
                                                    <Typography variant='subtitle2' className={classes.transactionMetricValue}>
                                                        {getDuration(item)}
                                                    </Typography>
                                                </Box>
                                                <Box className={classes.transactionMetricRow}>
                                                    <Box className={classes.transactionMetricIcon}>
                                                        <ChargingHistoryFlashIcon {...getTransactionIconProps()} />
                                                    </Box>
                                                    <Typography variant='subtitle2' className={classes.transactionMetricValue}>
                                                        {`${item.kwh} ${translationService.getTranslation(translatedData, 'KWH')}`}
                                                    </Typography>
                                                </Box>
                                                <Box className={classes.transactionMetricRow}>
                                                    <Box className={classes.transactionMetricIcon}>
                                                        <ChargingHistoryCardIcon {...getTransactionIconProps()} />
                                                    </Box>
                                                    <Typography variant='subtitle2' className={classes.transactionMetricValue}>
                                                        {`${item.price} ₪`}
                                                    </Typography>
                                                </Box>
                                                {/*
                                                    spec 000196 Round 4 — the charge ended but the vehicle is still
                                                    in the bay, so nothing has been collected and the price above
                                                    carries no parking yet. A second summary with the final price
                                                    follows when the vehicle is unplugged.
                                                */}
                                                {item.parkingPending && (
                                                    <Typography variant='caption' className={classes.parkingPendingNote} data-testid='receipt-parking-pending'>
                                                        {translationService.getTranslation(translatedData, 'parkingPending')}
                                                    </Typography>
                                                )}
                                            </Box>
                                        </Box>
                                    </Box>
                                </AppAccordionSummary>
                                <AppAccordionDetails>
                                    <Grid container spacing={1}>
                                        <Grid container item xs={4}>
                                            <Typography variant='subtitle2' className={classes.detailsLabel}>
                                                {translationService.getTranslation(translatedData, 'user')}
                                            </Typography>
                                        </Grid>
                                        <Grid container item xs={8}>
                                            <Typography variant='subtitle2' className={classes.detailsValue}>{item.driver !== null ? item.driver : item.user}</Typography>
                                        </Grid>
                                        <Grid container item xs={4}>
                                            <Typography variant='subtitle2' className={classes.detailsLabel}>
                                                {translationService.getTranslation(translatedData, 'startId')}
                                            </Typography>
                                        </Grid>
                                        <Grid container item xs={8}>
                                            <Typography variant='subtitle2' className={classes.detailsValue}>{item.tag_id}</Typography>
                                        </Grid>
                                        <Grid container item xs={4}>
                                            <Typography variant='subtitle2' className={classes.detailsLabel}>
                                                {translationService.getTranslation(translatedData, 'endId')}
                                            </Typography>
                                        </Grid>
                                        <Grid container item xs={8}>
                                            <Typography variant='subtitle2' className={classes.detailsValue}>{item.finishing_tag_id}</Typography>
                                        </Grid>
                                        <Grid container item xs={4}>
                                            <Typography variant='subtitle2' className={classes.detailsLabel}>
                                                {translationService.getTranslation(translatedData, 'transactionNum')}
                                            </Typography>
                                        </Grid>
                                        <Grid container item xs={8}>
                                            <Typography variant='subtitle2' className={classes.detailsValue}>{item.localTransactionId}</Typography>
                                        </Grid>
                                        <Grid container item xs={4}>
                                            <Typography variant='subtitle2' className={classes.detailsLabel}>
                                                {translationService.getTranslation(translatedData, 'charger')}
                                            </Typography>
                                        </Grid>
                                        <Grid container item xs={8}>
                                            <Typography variant='subtitle2' className={classes.detailsValue}>{item.serialNo}</Typography>
                                        </Grid>

                                        <Grid container item xs={4}>
                                            <Typography variant='subtitle2' className={classes.detailsLabel}>
                                                {translationService.getTranslation(translatedData, 'connectorNumber')}
                                            </Typography>
                                        </Grid>
                                        <Grid container item xs={8}>
                                            <Typography variant='subtitle2' className={classes.detailsValue}>{item.connectorNO}</Typography>
                                        </Grid>

                                        <Grid container item xs={4}>
                                            <Typography variant='subtitle2' className={classes.detailsLabel}>
                                                {translationService.getTranslation(translatedData, 'chargePointNickName')}
                                            </Typography>
                                        </Grid>
                                        <Grid container item xs={8}>
                                            <Typography variant='subtitle2' className={classes.detailsValue}>{item.nickname}</Typography>
                                        </Grid>

                                        {/* {item.chargingStrategyTotalDataList?.length > 0 && */}
                                        {item.chargingStrategyTotalDataList?.length > 0 && (
                                            <Grid container item xs={8}>
                                                <Typography variant='subtitle2' className={classes.detailsLabel}>
                                                    {translationService.getTranslation(translatedData, 'chargingStrategyTitle')}
                                                </Typography>
                                                {/*
                                                    spec 000196 (F-17) — translatedData was never
                                                    passed, so every label inside this breakdown
                                                    rendered blank on the receipt: a driver saw
                                                    three numbers with nothing to say what any of
                                                    them was for.

                                                    It takes the chargingAction dictionary, not this
                                                    screen's visualStepperComponent one — which is
                                                    presumably why it was omitted rather than wired
                                                    to the value already in scope.
                                                */}
                                                <ChargingStrategySummaryComponent
                                                    chargingStrategyTotalData={item.chargingStrategyTotalDataList}
                                                    translatedData={chargingActionTranslations}
                                                    // spec 000196 Round 5 (R5.B5) — the pricing model this session used, so the
                                                    // receipt names "דמי תפעול" / "דמי חניה לאחר סיום טעינה" when they apply.
                                                    chargingStrategy={{ serviceFeeEnabled: item.usesServiceFee === true, parkingFeeEnabled: item.usesParkingFee === true }}
                                                />
                                            </Grid>
                                        )}
                                    </Grid>
                                    {hasInvoiceUrl(item) && (
                                        <Box className={classes.invoiceAction}>
                                            <AppButton
                                                width='100%'
                                                className={classes.invoiceButton}
                                                icon={<BillingInvoicesTabIcon size={INVOICE_ICON_SIZE_PX} />}
                                                label={translationService.getTranslation(translatedData, 'downloadInvoice')}
                                                onClick={(event) => openInvoice(event, item.invoiceUrl)}
                                            />
                                        </Box>
                                    )}
                                </AppAccordionDetails>
                            </AppAccordion>
                        </div>
                    );
                })}
        </Box>
    );
};

const useStyles = makeStyles((theme) => ({
    summaryRow: {
        width: '100%',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        gap: theme.spacing(3),
    },
    timesContainer:{
        display: 'flex',
        gap: theme.spacing(1),
    },
    timelineDates: {
        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'space-between',
        alignItems: 'flex-start',
        gap: theme.spacing(0.3),
        minHeight: theme.spacing(9),
        flexShrink: 0,
    },
    timelineRail: {
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'space-between',
        minWidth: theme.spacing(3),
        minHeight: theme.spacing(9),
        paddingTop: theme.spacing(0.25),
        paddingBottom: theme.spacing(0.25),
        flexShrink: 0,
        gap: theme.spacing(0.5),
        // height: '100%',
    },
    timelineConnector: {
        width: theme.spacing(0.1),
        flex: 1,
        backgroundColor: theme.palette.grey[400],
    },
    timelineDot: {
        width: theme.spacing(1),
        height: theme.spacing(1),
        borderRadius: '50%',
        backgroundColor: theme.palette.grey[400],
        flexShrink: 0,
    },
    timelineIconWrap: {
        width: theme.spacing(3),
        height: theme.spacing(3),
        display: 'inline-flex',
        alignItems: 'center',
        justifyContent: 'center',
        flexShrink: 0,
    },
    transactionContent: {
        flex: 1,
        minWidth: 0,
        // height: '100%',
    },
    transactionMetrics: {
        display: 'flex',
        flexDirection: 'column',
        gap: theme.spacing(1),
    },
    transactionMetricRow: {
        display: 'flex',
        alignItems: 'center',
        gap: TRANSACTION_ICON_VALUE_GAP_PX,
        minHeight: theme.spacing(2.5),
    },
    transactionMetricIcon: {
        flex: `0 0 ${TRANSACTION_ICON_SLOT_WIDTH_PX}px`,
        width: TRANSACTION_ICON_SLOT_WIDTH_PX,
        height: TRANSACTION_ICON_SLOT_WIDTH_PX,
        display: 'inline-flex',
        alignItems: 'center',
        justifyContent: 'center',
        color: theme.palette.text.secondary,
    },
    transactionMetricValue: {
        flex: 1,
        minWidth: 0,
        lineHeight: 1.2,
    },
    parkingPendingNote: {
        color: theme.palette.text.secondary,
        lineHeight: 1.3,
    },
    detailsLabel: {
        fontWeight: theme.typography.fontWeightBold,
    },
    detailsValue: {
        fontWeight: theme.typography.fontWeightRegular,
    },
    // Full width of the details area, so the button shares the data rows' gutter. The gap above it equals
    // the details area's own bottom padding (AppAccordionDetails: theme.spacing(1.5)), so the button has
    // the same space above and below it.
    invoiceAction: {
        width: '100%',
        paddingTop: theme.spacing(1.5),
    },
    invoiceButton: {
        minHeight: theme.spacing(5),
        color: theme.palette.text.primary,
    },
    root: {
        marginBlock: theme.spacing(0.75),
    },
    listWrapper: {
        width: '100%',
    },
}));

VisualStepperComponent.propTypes = {};

const mapStateToProps = (state) => ({
    languageReducer: state.languageReducer,
});

const mapDispatchToProps = {};

export default connect(mapStateToProps, mapDispatchToProps)(VisualStepperComponent);
